import { useState, useEffect, useRef } from 'react'
import SessionImport from './SessionImport'
import { supabase } from '../lib/supabase'
import { signOut } from '../lib/supabase'

async function loadCompanies() {
  const { data } = await supabase.from('companies').select('*').order('created_at', { ascending: false })
  return data || []
}

async function loadCompanyData(companyId) {
  const [{ data: maps }, { data: scorecards }, { data: prioritiesList }, { data: members }, { data: weeklies }] = await Promise.all([
    supabase.from('accountability_maps').select('*').eq('company_id', companyId).limit(1),
    supabase.from('scorecards').select('*').eq('company_id', companyId).limit(1),
    supabase.from('priorities').select('*').eq('company_id', companyId).limit(1),
    supabase.from('company_members').select('*').eq('company_id', companyId),
    supabase.from('weekly_meetings').select('*').eq('company_id', companyId).order('updated_at', { ascending: false }).limit(5),
  ])
  return {
    map: maps?.[0] || null,
    scorecard: scorecards?.[0] || null,
    priorities: prioritiesList?.[0] || null,
    members: members || [],
    weeklies: weeklies || []
  }
}

async function saveCompanyData(companyId, tool, data) {
  const table = { map: 'accountability_maps', scorecard: 'scorecards', priorities: 'priorities', vision: 'vision_maps' }[tool]
  const dataKey = { map: 'map_data', scorecard: 'scorecard_data', priorities: 'priorities_data', vision: 'vision_data' }[tool]
  const constraintName = {
    map: 'accountability_maps_company_id_key',
    scorecard: 'scorecards_company_id_key',
    priorities: 'priorities_company_id_key',
    vision: 'vision_maps_company_id_key'
  }[tool]
  const { data: { session } } = await supabase.auth.getSession()
  const userId = session?.user?.id
  await supabase.from(table).upsert(
    { company_id: companyId, user_id: userId, [dataKey]: data, updated_at: new Date().toISOString() },
    { onConflict: 'company_id' }
  )
}

function getCurrentQuarter() {
  const now = new Date()
  return `Q${Math.ceil((now.getMonth() + 1) / 3)} ${now.getFullYear()}`
}

function getHealthScore(scorecard, priorities) {
  let score = 0, factors = 0
  if (scorecard?.metrics?.length) {
    const statuses = scorecard.metrics.map(m => {
      const keys = Object.keys(m.entries || {}).sort().reverse()
      return keys.length ? m.entries[keys[0]].status : null
    }).filter(Boolean)
    if (statuses.length) { score += statuses.filter(s => s === 'green').length / statuses.length; factors++ }
  }
  if (priorities?.quarters) {
    const q = Object.values(priorities.quarters).pop()
    if (q) {
      const rocks = [...(q.companyRocks || []), ...(q.individuals || []).flatMap(p => p.rocks || [])]
      if (rocks.length) { score += rocks.filter(r => r.status !== 'off_track').length / rocks.length; factors++ }
    }
  }
  if (!factors) return null
  const pct = Math.round((score / factors) * 100)
  if (pct >= 75) return { label: 'On track', color: '#166534', bg: '#F0FDF4', dot: '#22C55E' }
  if (pct >= 40) return { label: 'At risk', color: '#854D0E', bg: '#FEFCE8', dot: '#EAB308' }
  return { label: 'Needs attention', color: '#991B1B', bg: '#FEF2F2', dot: '#EF4444' }
}

let nid = 500

export default function AdminDashboard({ onPreviewClient }) {
  const [companies, setCompanies] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedCompany, setSelectedCompany] = useState(null)
  const [companyData, setCompanyData] = useState(null)
  const [activeTool, setActiveTool] = useState('map')
  const [saving, setSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState('')
  const [showAddClient, setShowAddClient] = useState(false)
  const [newCompanyName, setNewCompanyName] = useState('')
  const [newOwnerEmail, setNewOwnerEmail] = useState('')
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    load()
  }, [])

  async function load() {
    setLoading(true)
    const cos = await loadCompanies()
    setCompanies(cos)
    setLoading(false)
  }

  async function selectCompany(company) {
    setSelectedCompany(company)
    setActiveTool('map')
    setSaveStatus('')
    const data = await loadCompanyData(company.id)
    setCompanyData(data)
  }

  async function createCompany() {
    if (!newCompanyName.trim()) return
    setCreating(true)
    const { data, error } = await supabase.from('companies').insert({ name: newCompanyName.trim(), status: 'draft' }).select().single()
    if (!error && data) {
      // Create blank map data so company shows up
      await supabase.from('accountability_maps').upsert({
        company_id: data.id,
        map_data: { companyName: newCompanyName.trim(), visionary: { id: 'vis', title: 'Visionary', owner: '', pal: 7, resp: [], children: [] }, integrator: { id: 'int', title: 'Integrator', owner: '', pal: 2, resp: [], children: [] } },
        updated_at: new Date().toISOString()
      }, { onConflict: 'company_id' })
      setShowAddClient(false)
      setNewCompanyName('')
      // Auto-select the new company so sendInvite() targets it
      const fullData = await loadCompanyData(data.id)
      setSelectedCompany(data)
      setCompanyData(fullData)
      setActiveTool('map')
      // If admin supplied an owner email, invite them now.
      // sendInvite will create the company_members row AND flip
      // companies.status to 'active'.
      const ownerEmail = newOwnerEmail.trim()
      if (ownerEmail) {
        await sendInvite(ownerEmail, '')
      }
      setNewOwnerEmail('')
      await load()
    }
    setCreating(false)
  }

  async function handleSave(tool, data) {
    setSaving(true)
    setSaveStatus('saving')
    await saveCompanyData(selectedCompany.id, tool, data)
    // Refresh company data
    const refreshed = await loadCompanyData(selectedCompany.id)
    setCompanyData(refreshed)
    setSaveStatus('saved')
    setSaving(false)
    setTimeout(() => setSaveStatus(''), 3000)
  }

  async function sendInvite(email, name) {
    try {
      const res = await fetch('/api/invite-member', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({
          email,
          data: { company_name: selectedCompany.name, company_id: selectedCompany.id }
        })
      })
      if (res.ok) {
        const { data: userData } = await res.json()
        // Add to company_members
        await supabase.from('company_members').upsert({
          company_id: selectedCompany.id,
          email,
          name: name || '',
          status: 'invited',
          user_id: userData?.id || null
        }, { onConflict: 'company_id,email' })
        // Mark company as active
        await supabase.from('companies').update({ status: 'active' }).eq('id', selectedCompany.id)
        const refreshed = await loadCompanyData(selectedCompany.id)
        setCompanyData(refreshed)
        await load()
        return { success: true }
      } else {
        const err = await res.json()
        return { success: false, error: err.msg || err.error_description || 'Error sending invite' }
      }
    } catch (e) {
      return { success: false, error: 'Network error. Try again.' }
    }
  }

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1E3320' }}>
      <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14 }}>Loading command center…</div>
    </div>
  )

  return (
    <div style={{ minHeight: '100vh', background: '#F5F2EC', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ background: '#1E3320', padding: '16px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 32, height: 32, background: 'rgba(255,255,255,0.1)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 1L1 4.5V11.5L8 15L15 11.5V4.5L8 1Z" stroke="#B8965A" strokeWidth="1.2" fill="none"/></svg>
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>Hemlock Group</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Command center</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {selectedCompany && (
            <button onClick={() => { setSelectedCompany(null); setCompanyData(null) }} style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', background: 'none', border: 'none', cursor: 'pointer' }}>
              ← All clients
            </button>
          )}
          <button onClick={signOut} style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', background: 'none', border: 'none', cursor: 'pointer' }}>Sign out</button>
        </div>
      </div>

      {!selectedCompany ? (
        // CLIENT LIST
        <div style={{ padding: 40, maxWidth: 1100, margin: '0 auto', width: '100%' }}>
          <div style={{ marginBottom: 32, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <h1 style={{ fontSize: 24, fontWeight: 600, color: '#1E3320', marginBottom: 4 }}>Your clients</h1>
              <div style={{ fontSize: 13, color: '#888780' }}>{companies.length} {companies.length === 1 ? 'client' : 'clients'}</div>
            </div>
            <button onClick={() => setShowAddClient(true)} style={{ fontSize: 13, padding: '10px 20px', borderRadius: 7, border: 'none', background: '#1E3320', color: '#fff', fontWeight: 500, cursor: 'pointer' }}>
              + Add client
            </button>
          </div>

          {companies.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 0', color: '#888780', fontSize: 14 }}>
              No clients yet. Click "+ Add client" to get started.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
              {companies.map(company => (
                <CompanyCard
                  key={company.id}
                  company={company}
                  onSelect={() => selectCompany(company)}
                  onPreview={() => onPreviewClient && onPreviewClient({ id: company.id, companyName: company.name })}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        // COMPANY EDITOR
        <CompanyEditor
          company={selectedCompany}
          data={companyData}
          activeTool={activeTool}
          setActiveTool={setActiveTool}
          onSave={handleSave}
          onSendInvite={sendInvite}
          saving={saving}
          saveStatus={saveStatus}
        />
      )}

      {/* Add client modal */}
      {showAddClient && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: '#2C2C2A' }}>Add new client</h2>
              <button onClick={() => { setShowAddClient(false); setNewCompanyName(''); setNewOwnerEmail('') }} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 20, color: '#aaa' }}>×</button>
            </div>
            <p style={{ fontSize: 13, color: '#888780', marginBottom: 20, lineHeight: 1.6 }}>
              Create a workspace first. You'll pre-fill their data before sending any invites.
            </p>
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 11, fontWeight: 500, color: '#888780', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.06em' }}>Company name</label>
              <input
                value={newCompanyName}
                onChange={e => setNewCompanyName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && createCompany()}
                placeholder="e.g. GIGGS"
                autoFocus
                style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #D0CDC7', borderRadius: 6, outline: 'none' }}
              />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 11, fontWeight: 500, color: '#888780', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.06em' }}>Owner email <span style={{ textTransform: 'none', color: '#B8B5AE', fontWeight: 400 }}>(optional — invite now)</span></label>
              <input
                value={newOwnerEmail}
                onChange={e => setNewOwnerEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && createCompany()}
                placeholder="owner@company.com"
                type="email"
                style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #D0CDC7', borderRadius: 6, outline: 'none' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { setShowAddClient(false); setNewCompanyName(''); setNewOwnerEmail('') }} style={{ flex: 1, padding: '10px', fontSize: 13, border: '1px solid #D0CDC7', borderRadius: 6, background: 'transparent', color: '#888', cursor: 'pointer' }}>Cancel</button>
              <button onClick={createCompany} disabled={creating || !newCompanyName.trim()} style={{ flex: 2, padding: '10px', fontSize: 13, border: 'none', borderRadius: 6, background: creating || !newCompanyName.trim() ? '#aaa' : '#1E3320', color: '#fff', fontWeight: 500, cursor: 'pointer' }}>
                {creating ? 'Creating…' : 'Create workspace →'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function CompanyCard({ company, onSelect, onPreview }) {
  const isDraft = company.status === 'draft'
  return (
    <div style={{ background: '#fff', border: '1px solid #EBE7DE', borderRadius: 10, padding: 24, transition: 'box-shadow .2s' }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ width: 40, height: 40, borderRadius: 8, background: isDraft ? '#F1EFE8' : '#1E3320', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 600, color: isDraft ? '#888' : '#fff' }}>
          {company.name.charAt(0).toUpperCase()}
        </div>
        <span style={{ fontSize: 11, fontWeight: 500, padding: '3px 10px', borderRadius: 999, background: isDraft ? '#F1EFE8' : '#F0FDF4', color: isDraft ? '#888780' : '#166534' }}>
          {isDraft ? 'Draft' : 'Active'}
        </span>
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color: '#2C2C2A', marginBottom: 4 }}>{company.name}</div>
      <div style={{ fontSize: 12, color: '#888780', marginBottom: 16 }}>
        {isDraft ? 'Workspace ready — invite team when ready' : 'Active client'}
      </div>
      <div style={{ display: 'flex', gap: 8, paddingTop: 14, borderTop: '1px solid #F1EFE8' }}>
        <button onClick={onSelect} style={{ flex: 1, fontSize: 11, padding: '7px 0', borderRadius: 5, border: '1px solid #D0CDC7', background: 'transparent', color: '#4A4840', cursor: 'pointer' }}>
          {isDraft ? 'Pre-fill workspace' : 'Edit workspace'}
        </button>
        {!isDraft && (
          <button onClick={onPreview} style={{ flex: 1, fontSize: 11, padding: '7px 0', borderRadius: 5, border: '1px solid #B8965A', background: 'transparent', color: '#854F0B', cursor: 'pointer' }}>
            👁 View as client
          </button>
        )}
      </div>
    </div>
  )
}

function CompanyEditor({ company, data, activeTool, setActiveTool, onSave, onSendInvite, saving, saveStatus }) {
  const [refreshKey, setRefreshKey] = useState(0)
  const tools = [
    { id: 'session', label: '📸 Session import', special: true },
    { id: 'map', label: 'Accountability Map' },
    { id: 'scorecard', label: 'Scorecard' },
    { id: 'priorities', label: '90 Day Priorities' },
    { id: 'invite', label: 'Invite team', special: true },
  ]

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #EBE7DE', padding: '16px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: '#1E3320', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, color: '#fff' }}>
            {company.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#2C2C2A' }}>{company.name}</div>
            <div style={{ fontSize: 11, color: company.status === 'draft' ? '#B8965A' : '#3B6D11' }}>
              {company.status === 'draft' ? '● Draft — pre-fill before inviting' : '● Active'}
            </div>
          </div>
        </div>
        <span style={{ fontSize: 12, color: saveStatus === 'saved' ? '#166534' : saveStatus === 'saving' ? '#854F0B' : '#888' }}>
          {saveStatus === 'saved' ? '✓ Auto-saved' : saveStatus === 'saving' ? '↑ Saving…' : '● Changes will auto-save'}
        </span>
      </div>

      {/* Tabs */}
      <div style={{ background: '#fff', borderBottom: '1px solid #EBE7DE', padding: '0 32px', display: 'flex', gap: 0 }}>
        {tools.map(t => (
          <button key={t.id} onClick={() => setActiveTool(t.id)} style={{
            padding: '12px 20px', fontSize: 13, border: 'none', background: 'none', cursor: 'pointer',
            color: activeTool === t.id ? (t.special ? '#B8965A' : '#1E3320') : '#888780',
            borderBottom: activeTool === t.id ? `2px solid ${t.special ? '#B8965A' : '#1E3320'}` : '2px solid transparent',
            fontWeight: activeTool === t.id ? 500 : 400
          }}>{t.label}</button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {activeTool === 'session' && <SessionImport company={company} onApply={async (section, result) => {
          if (section === 'map' && result.accountabilityMap) {
            const m = result.accountabilityMap
            const mapData = {
              companyName: m.companyName || company.name,
              visionary: { id: 'vis', title: 'Visionary', owner: m.visionary?.owner || '', pal: 7, resp: m.visionary?.responsibilities || [], children: [] },
              integrator: { id: 'int', title: 'Integrator', owner: m.integrator?.owner || '', pal: 2, resp: m.integrator?.responsibilities || [], children: (m.departments || []).map((d, i) => ({ id: 1000+i, title: d.title, owner: d.owner || '', pal: i % 8, resp: d.responsibilities || [], children: [] })) }
            }
            await onSave('map', mapData)
            setTimeout(() => setRefreshKey(k => k + 1), 500)
          } else if (section === 'scorecard' && result.scorecard) {
            const scorecardData = { metrics: (result.scorecard.metrics || []).map((m, i) => ({ id: 2000+i, name: m.name, owner: m.owner || '', goal: m.goal || '', cadence: m.cadence || 'weekly', entries: {} })) }
            await onSave('scorecard', scorecardData)
            setTimeout(() => setRefreshKey(k => k + 1), 500)
          } else if (section === 'priorities' && result.priorities) {
            const q = `Q${Math.ceil((new Date().getMonth() + 1) / 3)} ${new Date().getFullYear()}`
            const prioritiesData = { quarters: { [q]: { companyRocks: (result.priorities.companyRocks || []).map((r, i) => ({ id: 3000+i, title: r.title, owner: r.owner || '', status: 'on_track' })), individuals: (result.priorities.individuals || []).map((p, i) => ({ id: 4000+i, name: p.name, rocks: (p.rocks || []).map((r, ri) => ({ id: 5000+i*100+ri, title: r.title, status: 'on_track' })) })) } } }
            await onSave('priorities', prioritiesData)
            setTimeout(() => setRefreshKey(k => k + 1), 500)
          }
        }} />}
        {activeTool === 'map' && data && <AdminMapEditor key={`map-${refreshKey}`} company={company} mapData={data.map?.map_data} onSave={d => onSave('map', d)} saving={saving} />}
        {activeTool === 'scorecard' && data && <AdminScorecardEditor key={`sc-${refreshKey}`} scorecardData={data.scorecard?.scorecard_data} onSave={d => onSave('scorecard', d)} saving={saving} />}
        {activeTool === 'priorities' && data && <AdminPrioritiesEditor key={`pr-${refreshKey}`} prioritiesData={data.priorities?.priorities_data} onSave={d => onSave('priorities', d)} saving={saving} />}
        {activeTool === 'invite' && data && <InviteTeam company={company} members={data.members} onSendInvite={onSendInvite} />}
      </div>
    </div>
  )
}

// ─── Invite Team ──────────────────────────────────────────────────────────
function InviteTeam({ company, members, onSendInvite }) {
  const [invites, setInvites] = useState([{ name: '', email: '' }])
  const [sending, setSending] = useState(false)
  const [results, setResults] = useState({})

  async function sendAll() {
    setSending(true)
    const pending = invites.filter(i => i.email.trim())
    for (const inv of pending) {
      const result = await onSendInvite(inv.email.trim(), inv.name.trim())
      setResults(prev => ({ ...prev, [inv.email]: result }))
    }
    setSending(false)
  }

  return (
    <div style={{ padding: 32, maxWidth: 700, margin: '0 auto' }}>
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: '#2C2C2A', marginBottom: 8 }}>Invite team to {company.name}</h2>
        <p style={{ fontSize: 13, color: '#888780', lineHeight: 1.6 }}>
          Add the leadership team members who should have access to this workspace. Each person will receive an email to set their password and log in.
        </p>
      </div>

      {/* Existing members */}
      {members.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em', color: '#888780', marginBottom: 12 }}>Team members</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {members.map(m => (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: '1px solid #EBE7DE', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#1E3320', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, color: '#fff', flexShrink: 0 }}>
                  {(m.name || m.email).charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#2C2C2A' }}>{m.name || '—'}</div>
                  <div style={{ fontSize: 11, color: '#888780' }}>{m.email}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 500, padding: '3px 10px', borderRadius: 999, background: m.status === 'active' ? '#F0FDF4' : '#FEFCE8', color: m.status === 'active' ? '#166534' : '#854D0E' }}>
                  {m.status === 'active' ? 'Active' : 'Invited'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite form */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em', color: '#888780', marginBottom: 12 }}>
          {members.length > 0 ? 'Send more invites' : 'Invite team members'}
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 6, padding: '0 2px' }}>
          <span style={{ flex: 1, fontSize: 10, fontWeight: 500, color: '#aaa', textTransform: 'uppercase', letterSpacing: '.06em' }}>Name</span>
          <span style={{ flex: 2, fontSize: 10, fontWeight: 500, color: '#aaa', textTransform: 'uppercase', letterSpacing: '.06em' }}>Email</span>
          <span style={{ width: 32 }}></span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {invites.map((inv, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input value={inv.name} onChange={e => { const arr = [...invites]; arr[i].name = e.target.value; setInvites(arr) }}
                placeholder="Nikki Sanz" style={{ flex: 1, padding: '8px 10px', fontSize: 13, border: '1px solid #D0CDC7', borderRadius: 6, outline: 'none' }} />
              <input value={inv.email} onChange={e => { const arr = [...invites]; arr[i].email = e.target.value; setInvites(arr) }}
                placeholder="nikki@giggs.com" type="email" style={{ flex: 2, padding: '8px 10px', fontSize: 13, border: `1px solid ${results[inv.email] ? (results[inv.email].success ? '#86EFAC' : '#FCA5A5') : '#D0CDC7'}`, borderRadius: 6, outline: 'none' }} />
              {invites.length > 1 && (
                <button onClick={() => setInvites(invites.filter((_, ii) => ii !== i))} style={{ width: 32, height: 36, border: 'none', background: 'none', cursor: 'pointer', color: '#ccc', fontSize: 18, flexShrink: 0 }}>×</button>
              )}
              {results[inv.email] && (
                <span style={{ fontSize: 11, color: results[inv.email].success ? '#166534' : '#991B1B', flexShrink: 0 }}>
                  {results[inv.email].success ? '✓ Sent' : '✗ ' + results[inv.email].error}
                </span>
              )}
            </div>
          ))}
        </div>
        <button onClick={() => setInvites([...invites, { name: '', email: '' }])}
          style={{ fontSize: 11, padding: '5px 12px', border: '1px solid #D0CDC7', borderRadius: 5, background: 'transparent', color: '#888', cursor: 'pointer', marginTop: 10 }}>
          + Add another person
        </button>
      </div>

      <button onClick={sendAll} disabled={sending || !invites.some(i => i.email.trim())}
        style={{ width: '100%', padding: '12px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 7, background: sending || !invites.some(i => i.email.trim()) ? '#aaa' : '#1E3320', color: '#fff', cursor: 'pointer' }}>
        {sending ? 'Sending invites…' : `Send ${invites.filter(i => i.email.trim()).length} invite${invites.filter(i => i.email.trim()).length !== 1 ? 's' : ''}`}
      </button>

      <div style={{ marginTop: 16, fontSize: 11, color: '#aaa', textAlign: 'center', lineHeight: 1.6 }}>
        Each person receives an email to set their password. They'll see the workspace you've already filled out.
      </div>
    </div>
  )
}

// ─── Map Editor ──────────────────────────────────────────────────────────
const PAL_COLORS = [
  { bg: '#E6F1FB', border: '#378ADD', title: '#0C447C' },
  { bg: '#EEEDFE', border: '#7F77DD', title: '#3C3489' },
  { bg: '#E1F5EE', border: '#1D9E75', title: '#085041' },
  { bg: '#FAECE7', border: '#D85A30', title: '#712B13' },
  { bg: '#FBEAF0', border: '#D4537E', title: '#72243E' },
  { bg: '#FAEEDA', border: '#BA7517', title: '#633806' },
  { bg: '#EAF3DE', border: '#639922', title: '#27500A' },
  { bg: '#F1EFE8', border: '#888780', title: '#444441' },
]

function AdminMapEditor({ company, mapData: initialMapData, onSave, saving }) {
  const defaultMap = {
    companyName: company.name,
    visionary: { id: 'vis', title: 'Visionary', owner: '', pal: 7, resp: [], children: [] },
    integrator: { id: 'int', title: 'Integrator', owner: '', pal: 2, resp: [], children: [] }
  }
  const [mapData, setMapData] = useState(initialMapData || defaultMap)
  const autoSaveRef = useRef(null)

  useEffect(() => {
    if (!mapData) return
    clearTimeout(autoSaveRef.current)
    autoSaveRef.current = setTimeout(() => onSave(mapData), 2000)
    return () => clearTimeout(autoSaveRef.current)
  }, [mapData])

  function update(fn) {
    setMapData(prev => { const next = JSON.parse(JSON.stringify(prev)); fn(next); return next })
  }

  return (
    <div style={{ padding: 32, maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: '#2C2C2A' }}>Accountability Map</h2>
        <button onClick={() => onSave(mapData)} disabled={saving} style={{ fontSize: 12, padding: '8px 20px', borderRadius: 6, border: 'none', background: saving ? '#aaa' : '#1E3320', color: '#fff', cursor: 'pointer' }}>
          {saving ? 'Saving…' : 'Save to workspace'}
        </button>
      </div>

      <Field label="Company name">
        <input value={mapData.companyName || ''} onChange={e => update(d => { d.companyName = e.target.value })} style={inputStyle} />
      </Field>

      {/* Visionary */}
      <NodeCard label="Visionary" node={mapData.visionary} onChange={(field, val) => update(d => { d.visionary[field] = val })} />

      {/* Integrator */}
      <NodeCard label="Integrator" node={mapData.integrator} onChange={(field, val) => update(d => { d.integrator[field] = val })} />

      {/* Departments */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#888780', textTransform: 'uppercase', letterSpacing: '.07em' }}>Functions / Departments</div>
          <button onClick={() => update(d => {
            if (!d.integrator.children) d.integrator.children = []
            d.integrator.children.push({ id: nid++, title: 'New function', owner: '', pal: 0, resp: [], children: [] })
          })} style={{ fontSize: 11, padding: '5px 12px', border: '1px solid #D0CDC7', borderRadius: 5, background: 'transparent', cursor: 'pointer', color: '#4A4840' }}>+ Add function</button>
        </div>
        {(mapData.integrator?.children || []).map((child, i) => (
          <div key={child.id} style={{ background: '#fff', border: `1px solid ${PAL_COLORS[child.pal || 0].border}`, borderRadius: 8, padding: 16, marginBottom: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, marginBottom: 10, alignItems: 'end' }}>
              <Field label="Function">
                <input value={child.title || ''} onChange={e => update(d => { d.integrator.children[i].title = e.target.value })} style={inputStyle} placeholder="e.g. Marketing" />
              </Field>
              <Field label="Owner">
                <input value={child.owner || ''} onChange={e => update(d => { d.integrator.children[i].owner = e.target.value })} style={inputStyle} placeholder="Person's name" />
              </Field>
              <button onClick={() => update(d => { d.integrator.children.splice(i, 1) })} style={{ height: 36, padding: '0 10px', border: '1px solid #FCA5A5', borderRadius: 6, background: 'transparent', color: '#B91C1C', cursor: 'pointer', fontSize: 12 }}>Remove</button>
            </div>
            <Field label="Color">
              <div style={{ display: 'flex', gap: 6 }}>
                {PAL_COLORS.map((p, pi) => (
                  <div key={pi} onClick={() => update(d => { d.integrator.children[i].pal = pi })}
                    style={{ width: 20, height: 20, borderRadius: '50%', background: p.bg, outline: `2px solid ${p.border}`, outlineOffset: 1, border: child.pal === pi ? `2px solid ${p.title}` : '2px solid transparent', cursor: 'pointer' }} />
                ))}
              </div>
            </Field>
            <Field label="Responsibilities (one per line)" style={{ marginTop: 10 }}>
              <textarea value={(child.resp || []).join('\n')}
                onChange={e => update(d => { d.integrator.children[i].resp = e.target.value.split('\n') })}
                onBlur={e => update(d => { d.integrator.children[i].resp = e.target.value.split('\n').map(s => s.trim()).filter(Boolean) })}
                style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} placeholder="One per line" />
            </Field>
            {/* Sub-children */}
            {(child.children || []).length > 0 && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #EBE7DE' }}>
                <div style={{ fontSize: 11, fontWeight: 500, color: '#888780', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>Reports to {child.title}</div>
                {(child.children || []).map((sub, si) => (
                  <div key={sub.id} style={{ background: '#F9F7F4', border: '1px solid #EBE7DE', borderRadius: 6, padding: 12, marginBottom: 8 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginBottom: 8 }}>
                      <input value={sub.title || ''} onChange={e => update(d => { d.integrator.children[i].children[si].title = e.target.value })} style={inputStyle} placeholder="Function" />
                      <input value={sub.owner || ''} onChange={e => update(d => { d.integrator.children[i].children[si].owner = e.target.value })} style={inputStyle} placeholder="Owner" />
                      <button onClick={() => update(d => { d.integrator.children[i].children.splice(si, 1) })} style={{ height: 36, padding: '0 10px', border: '1px solid #FCA5A5', borderRadius: 6, background: 'transparent', color: '#B91C1C', cursor: 'pointer', fontSize: 12 }}>×</button>
                    </div>
                    <Field label="Responsibilities (one per line)">
                      <textarea value={(sub.resp || []).join('\n')}
                        onChange={e => update(d => { d.integrator.children[i].children[si].resp = e.target.value.split('\n') })}
                        onBlur={e => update(d => { d.integrator.children[i].children[si].resp = e.target.value.split('\n').map(s => s.trim()).filter(Boolean) })}
                        style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} placeholder="One per line" />
                    </Field>
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => update(d => {
              if (!d.integrator.children[i].children) d.integrator.children[i].children = []
              d.integrator.children[i].children.push({ id: nid++, title: 'New function', owner: '', pal: child.pal, resp: [], children: [] })
            })} style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #D0CDC7', borderRadius: 5, background: 'transparent', cursor: 'pointer', color: '#4A4840', marginTop: 10 }}>
              + Add child function
            </button>
          </div>
        ))}
      </div>
      <button onClick={() => onSave(mapData)} disabled={saving} style={{ width: '100%', padding: '11px', fontSize: 14, fontWeight: 500, background: saving ? '#aaa' : '#1E3320', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
        {saving ? 'Saving…' : 'Save to workspace'}
      </button>
    </div>
  )
}

function NodeCard({ label, node, onChange }) {
  return (
    <div style={{ background: '#F9F7F4', border: '1px solid #EBE7DE', borderRadius: 8, padding: 20, marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#888780', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 12 }}>{label}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <Field label="Function title">
          <input value={node?.title || ''} onChange={e => onChange('title', e.target.value)} style={inputStyle} placeholder={`e.g. ${label}`} />
        </Field>
        <Field label="Owner">
          <input value={node?.owner || ''} onChange={e => onChange('owner', e.target.value)} style={inputStyle} placeholder="Person's name" />
        </Field>
      </div>
      <Field label="Responsibilities (one per line)">
        <textarea value={(node?.resp || []).join('\n')}
          onChange={e => onChange('resp', e.target.value.split('\n'))}
          onBlur={e => onChange('resp', e.target.value.split('\n').map(s => s.trim()).filter(Boolean))}
          style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} placeholder="One per line" />
      </Field>
    </div>
  )
}

// ─── Scorecard Editor ──────────────────────────────────────────────────────
function AdminScorecardEditor({ scorecardData: initial, onSave, saving }) {
  const [data, setData] = useState(initial || { metrics: [] })
  const autoSaveRef = useRef(null)

  useEffect(() => {
    if (!data) return
    clearTimeout(autoSaveRef.current)
    autoSaveRef.current = setTimeout(() => onSave(data), 2000)
    return () => clearTimeout(autoSaveRef.current)
  }, [data])

  function update(fn) { setData(prev => { const next = JSON.parse(JSON.stringify(prev)); fn(next); return next }) }
  return (
    <div style={{ padding: 32, maxWidth: 800, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: '#2C2C2A' }}>Scorecard metrics</h2>
        <button onClick={() => onSave(data)} disabled={saving} style={{ fontSize: 12, padding: '8px 20px', borderRadius: 6, border: 'none', background: saving ? '#aaa' : '#1E3320', color: '#fff', cursor: 'pointer' }}>{saving ? 'Saving…' : 'Save to workspace'}</button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
        {(data.metrics || []).map((m, i) => (
          <div key={m.id} style={{ background: '#fff', border: '1px solid #EBE7DE', borderRadius: 8, padding: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto auto', gap: 10, alignItems: 'end' }}>
              <Field label="Metric name"><input value={m.name || ''} onChange={e => update(d => { d.metrics[i].name = e.target.value })} style={inputStyle} placeholder="e.g. Revenue" /></Field>
              <Field label="Owner"><input value={m.owner || ''} onChange={e => update(d => { d.metrics[i].owner = e.target.value })} style={inputStyle} placeholder="Name" /></Field>
              <Field label="Goal"><input value={m.goal || ''} onChange={e => update(d => { d.metrics[i].goal = e.target.value })} style={inputStyle} placeholder="e.g. $50k" /></Field>
              <Field label="Cadence">
                <select value={m.cadence || 'weekly'} onChange={e => update(d => { d.metrics[i].cadence = e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </Field>
              <button onClick={() => update(d => { d.metrics.splice(i, 1) })} style={{ height: 36, padding: '0 10px', border: '1px solid #FCA5A5', borderRadius: 6, background: 'transparent', color: '#B91C1C', cursor: 'pointer', fontSize: 12, alignSelf: 'flex-end' }}>Remove</button>
            </div>
          </div>
        ))}
      </div>
      <button onClick={() => update(d => { d.metrics.push({ id: nid++, name: '', owner: '', goal: '', cadence: 'weekly', entries: {} }) })} style={{ fontSize: 12, padding: '8px 16px', border: '1px solid #D0CDC7', borderRadius: 6, background: 'transparent', cursor: 'pointer', color: '#4A4840', marginBottom: 24 }}>+ Add metric</button>
      <button onClick={() => onSave(data)} disabled={saving} style={{ width: '100%', padding: '11px', fontSize: 14, fontWeight: 500, background: saving ? '#aaa' : '#1E3320', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>{saving ? 'Saving…' : 'Save to workspace'}</button>
    </div>
  )
}

// ─── Priorities Editor ─────────────────────────────────────────────────────
function AdminPrioritiesEditor({ prioritiesData: initial, onSave, saving }) {
  const q = getCurrentQuarter()
  const [data, setData] = useState(initial || { quarters: { [q]: { companyRocks: [], individuals: [] } } })
  const autoSaveRef = useRef(null)

  useEffect(() => {
    if (!data) return
    clearTimeout(autoSaveRef.current)
    autoSaveRef.current = setTimeout(() => onSave(data), 2000)
    return () => clearTimeout(autoSaveRef.current)
  }, [data])

  function update(fn) { setData(prev => { const next = JSON.parse(JSON.stringify(prev)); fn(next); return next }) }
  function ensureQ(d) { if (!d.quarters[q]) d.quarters[q] = { companyRocks: [], individuals: [] } }
  const qData = data.quarters?.[q] || { companyRocks: [], individuals: [] }
  return (
    <div style={{ padding: 32, maxWidth: 800, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: '#2C2C2A' }}>90 Day Priorities — {q}</h2>
        <button onClick={() => onSave(data)} disabled={saving} style={{ fontSize: 12, padding: '8px 20px', borderRadius: 6, border: 'none', background: saving ? '#aaa' : '#1E3320', color: '#fff', cursor: 'pointer' }}>{saving ? 'Saving…' : 'Save to workspace'}</button>
      </div>
      <div style={{ fontSize: 12, color: '#888780', marginBottom: 24 }}>Pre-fill the team's rocks from your session.</div>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#2C2C2A', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 3, height: 16, background: '#1E3320', borderRadius: 2 }} /> Company rocks
        </div>
        {(qData.companyRocks || []).map((r, i) => (
          <div key={r.id} style={{ display: 'flex', gap: 10, marginBottom: 8, alignItems: 'center' }}>
            <input value={r.title || ''} onChange={e => update(d => { ensureQ(d); d.quarters[q].companyRocks[i].title = e.target.value })} style={{ ...inputStyle, flex: 1 }} placeholder="Rock description" />
            <input value={r.owner || ''} onChange={e => update(d => { ensureQ(d); d.quarters[q].companyRocks[i].owner = e.target.value })} style={{ ...inputStyle, width: 140 }} placeholder="Owner" />
            <button onClick={() => update(d => { ensureQ(d); d.quarters[q].companyRocks.splice(i, 1) })} style={{ height: 36, padding: '0 10px', border: '1px solid #FCA5A5', borderRadius: 6, background: 'transparent', color: '#B91C1C', cursor: 'pointer', fontSize: 12 }}>×</button>
          </div>
        ))}
        <button onClick={() => update(d => { ensureQ(d); d.quarters[q].companyRocks.push({ id: nid++, title: '', owner: '', status: 'on_track' }) })} style={{ fontSize: 11, padding: '5px 12px', border: '1px solid #D0CDC7', borderRadius: 5, background: 'transparent', cursor: 'pointer', color: '#4A4840' }}>+ Add rock</button>
      </div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#2C2C2A', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 3, height: 16, background: '#3B6D11', borderRadius: 2 }} /> Individual rocks
          </div>
          <button onClick={() => update(d => { ensureQ(d); d.quarters[q].individuals.push({ id: nid++, name: 'New person', rocks: [] }) })} style={{ fontSize: 11, padding: '5px 12px', border: '1px solid #D0CDC7', borderRadius: 5, background: 'transparent', cursor: 'pointer', color: '#4A4840' }}>+ Add person</button>
        </div>
        {(qData.individuals || []).map((person, pi) => (
          <div key={person.id} style={{ background: '#F9F7F4', border: '1px solid #EBE7DE', borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
            <div style={{ padding: '10px 14px', background: '#F1EFE8', borderBottom: '1px solid #EBE7DE', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <input value={person.name || ''} onChange={e => update(d => { ensureQ(d); d.quarters[q].individuals[pi].name = e.target.value })} style={{ fontSize: 14, fontWeight: 600, border: '1px solid #D0CDC7', borderRadius: 4, padding: '3px 8px', background: '#fff', outline: 'none' }} placeholder="Person name" />
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => update(d => { ensureQ(d); d.quarters[q].individuals[pi].rocks.push({ id: nid++, title: '', status: 'on_track' }) })} style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #D0CDC7', borderRadius: 5, background: 'transparent', cursor: 'pointer', color: '#4A4840' }}>+ Add rock</button>
                <button onClick={() => update(d => { ensureQ(d); d.quarters[q].individuals.splice(pi, 1) })} style={{ fontSize: 11, padding: '4px 8px', border: '1px solid #FCA5A5', borderRadius: 5, background: 'transparent', cursor: 'pointer', color: '#B91C1C' }}>×</button>
              </div>
            </div>
            <div style={{ padding: '10px 14px' }}>
              {(person.rocks || []).map((r, ri) => (
                <div key={r.id} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                  <input value={r.title || ''} onChange={e => update(d => { ensureQ(d); d.quarters[q].individuals[pi].rocks[ri].title = e.target.value })} style={{ ...inputStyle, flex: 1 }} placeholder="Rock description" />
                  <button onClick={() => update(d => { ensureQ(d); d.quarters[q].individuals[pi].rocks.splice(ri, 1) })} style={{ height: 34, padding: '0 8px', border: '1px solid #FCA5A5', borderRadius: 5, background: 'transparent', color: '#B91C1C', cursor: 'pointer', fontSize: 12 }}>×</button>
                </div>
              ))}
              {person.rocks.length === 0 && <div style={{ fontSize: 12, color: '#aaa' }}>No rocks yet</div>}
            </div>
          </div>
        ))}
      </div>
      <button onClick={() => onSave(data)} disabled={saving} style={{ width: '100%', padding: '11px', fontSize: 14, fontWeight: 500, background: saving ? '#aaa' : '#1E3320', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>{saving ? 'Saving…' : 'Save to workspace'}</button>
    </div>
  )
}

function Field({ label, children, style }) {
  return (
    <div style={{ ...style }}>
      <label style={{ fontSize: 11, fontWeight: 500, color: '#888780', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.06em' }}>{label}</label>
      {children}
    </div>
  )
}

const inputStyle = {
  width: '100%', padding: '8px 10px', fontSize: 13,
  border: '1px solid #D0CDC7', borderRadius: 6, outline: 'none',
  background: '#fff', color: '#2C2C2A', fontFamily: 'inherit'
}
